<template>
  <section class="map-wrapper" :class="{ desktop: $isDesktop }">
    <div v-if="!$isDesktop" class="pinned">
      <search-nav-bar
        :show-map="true"
        :results-count="resultsCount"
        :search-filters="searchFilters"
      />
      <search-summary-bar
        class="summary-bar"
        :search-filters="searchFilters"
        @search="search"
      />
    </div>

    <div class="action-buttons">
      <div v-if="errorOccurred" key="error" class="searching error">
        <span>An error occurred while searching</span>
      </div>
      <div v-if="$isDesktop" class="map-options">
        <button
          v-if="showRedoSearchButtonDesktop"
          key="redo"
          class="primary redo"
          data-test="maps-redo-button"
          @click="redoDesktopSearch"
        >
          <translate>Redo search for this area</translate>
        </button>
        <base-checkbox
          v-else
          :model-value="searchAsMapMoves"
          @change="toggleSearchAsMapMoves"
        >
          <translate>Search as I move</translate>
        </base-checkbox>
      </div>
      <template v-else>
        <button
          v-if="showRedoSearchButton"
          key="redo"
          class="primary redo"
          data-test="maps-redo-button"
          @click="updateSearchBounds()"
        >
          <translate>Redo search for this area</translate>
        </button>
        <div
          v-else-if="searching"
          key="search"
          class="searching"
          data-test="maps-searching"
        >
          <loading-spinner
            :stroke-color="'var(--colour-light-green)'"
            location="maps-searching"
          />
          <translate>Searching...</translate>
        </div>
      </template>
    </div>

    <translate
      v-if="!$isDesktop"
      tag="div"
      class="maps-info-panel"
      :data-test="`maps-results-count-${resultsCount}`"
      :tt="{ resultsCount }"
    >
      {resultsCount} results shown on map
    </translate>

    <client-only v-if="!$isDesktop">
      <div class="vue-portal-target portal-target">
        <portal-target name="portal-target" />
      </div>
    </client-only>

    <!-- Static fallback always visible -->
    <div
      v-if="$isDesktop"
      v-once
      class="pu-map search-results-map map-fallback"
    >
      <img
        fetchpriority="high"
        src="~/assets/map-placeholder.jpg"
        alt="Map loading placeholder"
        class="search-results-map map-placeholder"
      />
    </div>

    <!-- Real map -->
    <client-only>
      <campsite-results-map
        ref="campsiteResultsMap"
        v-bind="mapProps"
        :bounds-to-fit="boundsToFit"
        class="search-results-map"
        :class="{ 'map-real': $isDesktop }"
        @boundsChange="updateNewSearchBounds"
        @marker-clicked="$emit('marker-clicked', $event)"
        @marker-selected="updateSelectedCampsite"
        @abort="onAbort"
        @dragstart="onDragStart()"
        @userLocationChanged="updateSearchPoint($event)"
      >
        <template #dynamicPopup>
          <template v-if="selectedCampsite">
            <campsite-map-card
              :key="selectedCampsite.slug"
              :campsite="selectedCampsite"
              data-test="search-maps-popup"
            />
          </template>
          <div v-if="loadingMarkerInfoWindow" class="loading-infowindow">
            <loading-spinner location="maps-map-loading" />
          </div>
        </template>
      </campsite-results-map>
    </client-only>
    <!-- we need to use the resultsCount as the key to ensure the title is updated -->
    <nuxt-to-meta :key="resultsCount" :nuxt2meta="head" />
    <slot />
  </section>
</template>

<script lang="ts">
import { Bounds } from 'leaflet'
import { isEqual } from 'lodash-es'
import mapPlaceholderUrl from '~/assets/map-placeholder.jpg'
import { GeoPoint, getWKTPointFromGeoPoint } from '~/utility/geo/GeoLocation'
import { GeoBounds, computeBoundsFromLatLngs } from '~/utility/geo/bounds'
import { getPointFromStringOrDefault, getWKTPoint } from '~/utility/geo/latLng'
import { isLocalStorageAvailable } from '~/utility/isLocalStorageAvailable'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { getDefaultWithinForLocale } from '../../../utility/geo/distance-units'
import { logger } from '../../../utility/logger'
import { getMapSearchEngine } from '../../maps-search/domain/engine'
import { getCampsiteBySlug } from '../../maps-search/infrastructure/FetchCampsiteMapSearchService'
import type {
  CampsiteMapsSearchResult,
  SearchFilters,
} from '../../maps-search/schemas'
import {
  ResultCampsitesMapProps,
  ResultMapCampsite,
} from './campsites/CampsitesMapProps'

interface Point {
  lat: number
  lng: number
  isEmptyPoint?: boolean
}

// setting this high until we fix abort
const UNDATED_SEARCH_RESULTS_LIMIT = 99999

function getCampsiteForMap(
  campsite: CampsiteMapsSearchResult,
): ResultMapCampsite {
  const { lat, lng } = getPointFromStringOrDefault(campsite.point)
  return {
    ...campsite,
    point: { lat, lng },
  }
}

@Component({
  emits: ['mapMarkerOpened', 'searchFiltersUpdated'],
  setup() {
    const route = useRoute()
    const langCode = (route.params.lang as string) || 'en-gb'
    const { $gtm, $isDesktop } = useNuxtApp()
    // since we are doing a search anyway, let's use the search results to initilise the first set of markers
    const searchStore = useSearchStoreSetup(usePinia())
    const mapSearchEngine = process.client
      ? getMapSearchEngine(langCode, $gtm)
      : undefined

    // Add preload header for map placeholder during SSR
    const nuxtApp = useNuxtApp()
    if (process.server && nuxtApp?.ssrContext?.event?.node?.res && $isDesktop) {
      nuxtApp.ssrContext.event.node.res.setHeader(
        'Link',
        `<${mapPlaceholderUrl}>; rel="preload"; as="image"; fetchpriority="high"`,
      )
    }
    const mapStore = useMapStore(usePinia())

    return { searchStore, mapSearchEngine, mapStore }
  },
})
class MapsMain extends Vue {
  searchStore: ReturnType<typeof useSearchStoreSetup>
  mapSearchEngine?: ReturnType<typeof getMapSearchEngine>

  @Prop()
  searchFilters: SearchFilters

  @Prop({ required: false })
  visibleMarker?: string

  @Prop({ required: false })
  centre?: string

  isActive = true
  private hasFitBounds = false
  resultsCount: number = 0
  searching = true
  campsites: ResultMapCampsite[] = []

  point: Point | null = null

  newSearchBounds: '' | Bounds = ''
  private resultsBounds: GeoBounds | null = null
  currentPage = 0

  selectedCampsite: CampsiteMapsSearchResult | null = null
  loadingMarkerInfoWindow = false

  // this is the default value for the search as I move
  searchAsMapMoves = true
  private isRedoLabel = false

  get boundsToFit() {
    return this.searchFilters.bounds || this.resultsBounds
  }

  get showRedoSearchButtonDesktop() {
    return !this.searchAsMapMoves && this.isRedoLabel
  }

  checkSearchAsMapMoves() {
    if (
      isLocalStorageAvailable() &&
      localStorage.getItem('searchAsMapMoves') !== null
    ) {
      this.searchAsMapMoves =
        localStorage.getItem('searchAsMapMoves') === 'true'
    }
  }

  mounted() {
    logger('MapsMain.mounted', this.$route.fullPath)
    this.checkSearchAsMapMoves()
    logger('creating mapSearchEngine')
    if (typeof this.mapSearchEngine !== 'undefined') {
      // start the engine and listeners
      this.registerSearchEngineOnContext()
      this.mapSearchEngine.start()
    }
    this.search()
    this.syncStorageWithSearchFilters()
  }

  syncStorageWithSearchFilters() {
    this.updateStorage(
      this.searchFilters,
      this.searchFilters.hierarchyPath,
      this.searchFilters.area,
      this.searchFilters.placename,
    )
  }

  activated() {
    logger('MapsMain.activated')
    this.isActive = true
    this.search()
  }

  deactivated() {
    this.isActive = false
  }

  cleanup() {
    if (this.mapSearchEngine) {
      this.mapSearchEngine.stop()
      this.mapSearchEngine = undefined
    }
  }

  beforeUnmount() {
    if (this && this.cleanup) {
      this.cleanup()
    }
  }

  get head() {
    if (!this.$isDesktop) {
      return {
        title: this.title,
      }
    }
    return {}
  }

  get title() {
    return (this.$t('{resultsCount} results shown on map', {
      resultsCount: this.resultsCount,
    }) + ' | Pitchup.com') as string
  }

  get searchFiltersForNavBar() {
    return {
      ...this.searchFilters,
      fields: [
        'campsite_id',
        'categories',
        'category_ids',
        'hierarchy_text_short',
        'id',
        'name',
        'path',
        'cheapest_price',
        'point',
        'primary_photo',
        'rate_count',
        'rating',
        'rating100',
        'slug',
        'url',
      ],
    }
  }

  get mapCentre(): string | undefined {
    if (this.centre) {
      return this.centre
    }
    if (this.searchHasNoLocationData) {
      return this.mapCenterWKTPoint
    }
    if (this.searchHasAreaData && this.searchFilters.area) {
      return getWKTPointFromGeoPoint(this.searchFilters.area)
    }
    return undefined
  }

  private get searchHasAreaData() {
    return this.searchFilters.area && !this.searchFilters.area.isEmptyPoint
  }

  private get mapCenterWKTPoint() {
    const point = this.$i18n.localeInfo.mapCenter
    return getWKTPoint(point.lat, point.lng)
  }

  private get searchHasNoLocationData() {
    return (
      !this.searchFilters.hierarchyPath &&
      !this.searchFilters.bounds &&
      (!this.searchFilters.area || this.searchFilters.area.isEmptyPoint)
    )
  }

  updateSearchFiltersAndStorage(newFilters, hierarchyPath, area, placename) {
    this.updateStorage(newFilters, hierarchyPath, area, placename)
    this.onAbort()
    this.$emit('searchFiltersUpdated', newFilters)
    this.newSearchBounds = ''
  }

  updateStorage(newFilters, hierarchyPath, area, placename) {
    if (!isLocalStorageAvailable()) {
      return
    }
    if (newFilters.bounds) {
      return
    }
    const newSearchString = JSON.stringify({
      previousPath: hierarchyPath,
      previousPoint: {
        area,
        placename,
      },
    })
    localStorage.setItem('maps-previous-search', newSearchString)
  }

  updateSearchBounds(force?: boolean) {
    const bounds = this.getBounds()
    logger('MapsMain.updateSearchBounds', JSON.stringify(bounds))
    if (!force && (!this.isActive || !this.searchAsMapMoves)) {
      return
    }
    const { area, hierarchyPath, placename, ...filters } = this.searchFilters
    const newFilters = {
      ...filters,
      area: undefined,
      bounds,
      hierarchyPath: undefined,
      page: undefined,
    }
    this.updateSearchFiltersAndStorage(
      newFilters,
      hierarchyPath,
      area,
      placename,
    )
  }

  updateSearchPoint(point: GeoPoint) {
    logger('MapsMain.updateSearchPoint', JSON.stringify(point))
    if (!this.isActive) {
      return
    }
    this.point = point
    const { area, hierarchyPath, placename, ...filters } = this.searchFilters
    const defaultMiles = this.$config.public.defaultNearMeWithinRange
    const defaultInLocaleUnit = getDefaultWithinForLocale(
      defaultMiles,
      this.$i18n.locale,
    )
    const newFilters = {
      ...filters,
      within: defaultInLocaleUnit,
      area: point,
      bounds: undefined,
      hierarchyPath: undefined,
      page: undefined,
    }
    this.updateSearchFiltersAndStorage(
      newFilters,
      hierarchyPath,
      area,
      placename,
    )
  }

  toggleSearchAsMapMoves(event) {
    this.searchAsMapMoves = event
    if (isLocalStorageAvailable()) {
      localStorage.setItem(
        'searchAsMapMoves',
        this.searchAsMapMoves ? 'true' : 'false',
      )
    }
    if (event) {
      this.isRedoLabel = false
    }
  }

  redoDesktopSearch() {
    // this.searchAsMapMoves = false
    this.isRedoLabel = false
    this.updateSearchBounds(true)
  }

  setRedoLabel() {
    if (this.$isDesktop && !this.searchAsMapMoves && !this.isRedoLabel) {
      this.isRedoLabel = true
    }
  }

  updateNewSearchBounds(bounds: Bounds) {
    logger('updateNewSearchBounds', bounds)
    this.newSearchBounds = bounds
    if (this.$isDesktop && this.searchAsMapMoves) {
      this.updateSearchBounds()
    }
    this.setRedoLabel()
  }

  async updateSelectedCampsite(slug: string) {
    // When performing dated search we have all the data we need for the popup
    if (this.searchFilters.dates) {
      this.selectedCampsite =
        this.campsites.find((c) => c.slug === slug) || null
      return
    }
    this.loadingMarkerInfoWindow = true
    this.selectedCampsite = null
    const createGtmPerfEvent =
      window.performance && !window.navigator.userAgent.includes('gha2spon')
    if (createGtmPerfEvent) {
      window.performance.mark('infowindow-load-started')
    }
    const campsite = await this.fetchCampsiteById(slug)
    if (createGtmPerfEvent) {
      window.performance.mark('infowindow-load-complete')
      const loadDuration = window.performance.measure(
        'infowindow-load-duration',
        'infowindow-load-started',
        'infowindow-load-complete',
      )
      this.$gtm.push({
        event: 'map info window search duration',
        mapsSearchDuration: loadDuration?.duration,
        networkEffectiveType: window.navigator.connection?.effectiveType,
      })
    }

    this.selectedCampsite = campsite
    this.loadingMarkerInfoWindow = false
    this.$bubble('mapMarkerOpened', slug)
  }

  fetchCampsiteById(slug) {
    return getCampsiteBySlug(slug, this.$route.params.lang, this.$gtm)
  }

  @Watch('searchFilters')
  onSearchFiltersChange(newVal, oldVal) {
    if (!this.isActive || isEqual(newVal, oldVal)) {
      return
    }
    this.search()
    this.syncStorageWithSearchFilters()
  }

  search() {
    logger('MapsMain.search', this.isActive)
    this.onAbort()
    if (this.isActive && typeof this.mapSearchEngine !== 'undefined') {
      // reset fitbounds
      this.hasFitBounds = false
      this.mapSearchEngine.search({
        ...this.searchFilters,
        engine: this.$route.name?.includes('searchold')
          ? 1
          : this.$route.name?.includes('searchnew')
            ? 2
            : 0,
        limit: UNDATED_SEARCH_RESULTS_LIMIT,
        page: 1,
      })
      this.mapStore.resetUserInteraction()
    }
  }

  get isDated() {
    return !!this.searchFilters.dates
  }

  get mapProps(): ResultCampsitesMapProps {
    return {
      campsites: this.campsites,
      initialBounds: this.centre ? undefined : this.mapInitialBounds,
      visibleMarker: this.visibleMarker,
      centre: this.mapCentre,
      circle: this.circle,
      useClustering: true,
      searchFilters: this.searchFilters,
    }
  }

  get circle() {
    const radius =
      this.mapSearchEngine?.machine?.state?.context?.results?.within?.mi
    return this.point && !this.point.isEmptyPoint && typeof radius === 'number'
      ? {
          lat: this.point.lat,
          lng: this.point.lng,
          rangeMiles: radius,
          isEmptyPoint: false,
        }
      : null
  }

  get mapInitialBounds() {
    return this.searchFilters.bounds || this.resultsBounds
  }

  get showRedoSearchButton() {
    return this.newSearchBounds || this.errorOccurred
  }

  get errorOccurred() {
    if (typeof this.mapSearchEngine === 'undefined') {
      return false
    }
    if (this.mapSearchEngine.machine.initialized) {
      return this.mapSearchEngine.machine.state.context.errorOccurred
    }
    return false
  }

  private registerSearchEngineOnContext() {
    this.mapSearchEngine?.machine.onTransition(async (state, event) => {
      this.searching = state.matches('searching')
      const context = state.context
      const campsites = (context.campsites || []).map(getCampsiteForMap)
      // this is so the results aren't cleared when doing a new search
      // until it has something to show or it has finished and no results
      if (
        !(
          state.matches('results') ||
          (state.matches('searching') && campsites.length)
        )
      ) {
        return
      }
      const path = context.results?.path || ''
      const hierarchyPath = context.filters.hierarchyPath || ''
      await this.redirectToFixedPath(hierarchyPath, path)
      this.campsites = campsites
      this.resultsCount = campsites.length
      this.point = context?.results?.point || context?.filters?.area
      this.resultsBounds = computeBoundsFromLatLngs(
        campsites.map((campsite) => campsite.point),
      )
      if (campsites.length && !this.$route.query.bounds && this.resultsBounds) {
        this.hasFitBounds = true
      }
    })
  }

  async redirectToFixedPath(hierarchyPath: string, path: string) {
    if (hierarchyPath && path && hierarchyPath !== path) {
      const redirectPath = this.$route.fullPath.replace(hierarchyPath, path)
      await navigateTo(redirectPath)
    }
  }

  onAbort() {
    this.mapSearchEngine?.abort()
  }

  getBounds() {
    return this.$refs.campsiteResultsMap?.getBounds()
  }

  onDragStart() {
    logger('onDragStart', this.searchAsMapMoves)
    if (this.searchAsMapMoves) {
      this.onAbort()
      this.searchStore.abort()
    }
  }

  onUserLocationChanged(point: Point) {
    if (this.searchAsMapMoves) {
      this.onAbort()
      this.searchStore.abort()
      this.point = point
    }
  }
}

export default toNative(MapsMain)
</script>

<style scoped>
.map-wrapper {
  background-color: var(--colour-pale-blue);
  /* Method for setting div height to 100% when inside parent div that
     works in iOS12 */
  height: 100%;
  position: fixed;
  /* ------- */
  display: flex;
  flex-direction: column;
}

.map-wrapper :deep(.search-actions) {
  height: 13vw;
  position: relative;
}

.pu-map :deep(.leaflet-popup-content) {
  min-width: 22em;
}

.summary-bar {
  position: relative;
}

.action-buttons {
  bottom: 4%;
  color: #fff;
  left: 50%;
  position: fixed;
  transform: translate(-50%, -3%);
  white-space: normal;
  width: 100%;
  z-index: 99999;
}

.redo {
  display: block;
  width: auto;
  margin: auto;
}

.searching {
  align-items: center;
  background: black;
  border-radius: 0.3em;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin: auto;
  padding: 0.6em 1em 0.5em;
  width: max-content;
}

.searching span {
  font-size: var(--body);
  margin-left: 0.5em;
}

.searching.error {
  margin-bottom: 1em;
  background: rgba(0, 0, 0, 0.7);
}

.maps-info-panel {
  background: white;
  border-top: 1px solid #dedede;
  color: var(--colour-text-default);
  font-size: var(--small);
  width: 100%;
  text-align: center;
  padding: 0.2rem;
}

.loading-infowindow {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 6em;
}

.loading-infowindow :deep(.spinner) {
  height: 3em;
  width: auto;
}

.search-results-map {
  contain: layout paint;
}

.search-results-map :deep(.marker-cluster-small) {
  background-color: #ff760066;
}

.search-results-map :deep(.marker-cluster-small div) {
  background-color: #ff760044;
}

.search-results-map :deep(.marker-cluster-medium) {
  background-color: #ff760088;
}

.search-results-map :deep(.marker-cluster-medium div) {
  background-color: #ff760099;
}

.search-results-map :deep(.marker-cluster-large) {
  background-color: #ff7600aa;
}

.search-results-map :deep(.marker-cluster-large div) {
  background-color: #ff7600cc;
}

/* mobile landscape */
@media screen and (min-width: 320px) and (max-width: 998px) and (orientation: landscape) {
  .map-wrapper :deep(.search-actions) {
    height: 2.5em;
  }

  .maps-info-panel {
    top: 6.7em;
    height: 2.1em;
  }

  .pu-map.search-results-map {
    height: calc(100% - 6.7em) !important;
    top: 6.7em;
  }
}

/* desktop & tablet */
.map-wrapper.desktop {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.map-wrapper.desktop .redo {
  padding: 0.6em 1.2em;
  border-radius: 6em;
  box-shadow: none;
  border: 2px solid rgba(0, 0, 0, 0.2);
  background: var(--colour-dark-blue);
}

.map-wrapper.desktop :deep(.pu-geolocation) {
  position: absolute;
}

.desktop .action-buttons {
  top: 0;
  left: auto;
  position: absolute;
  transform: none;
  bottom: auto;
}

.desktop .map-options {
  position: absolute;
  left: 74px;
  top: 10px;
}

.desktop .map-options :deep(label) {
  border: 2px solid rgba(0, 0, 0, 0.2);
  background-clip: padding-box;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 3em;
  padding: 0.6em 1.3em;
}

.desktop .map-options :deep(input:checked ~ .checkbox-tick) {
  background-color: var(--colour-dark-blue);
  border-color: var(--colour-dark-blue);
}

.desktop .searching.error {
  margin: 1em;
}

.portal-target {
  z-index: 100000;
}

.map-placeholder {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background-color: var(--colour-pale-blue);
}

.leaflet-pane {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

.leaflet-tile-pane {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

.leaflet-layer {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

.leaflet-tile-container {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

.leaflet-tile {
  position: absolute;
  user-select: none;
}

.search-results-map {
  width: 100%;
  height: 100%;
  position: relative;
}

.placehoder-leaflet-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.leaflet-pane {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

.leaflet-tile-pane {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

.leaflet-layer {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

.leaflet-tile-container {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

.leaflet-tile {
  position: absolute;
  user-select: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

.map-fallback {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.map-real {
  z-index: 2;
}

/* .map-real {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  opacity: 0;
  animation: fadeIn 0.3s ease-in-out forwards;
} */

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

:deep(.leaflet-pane) {
  will-change: transform;
}
</style>
